import { isNil, isNotNil } from "@etudescarre/utils";
import {
  FileText as FileTextIcon,
  Paperclip as PaperclipIcon,
  PaperPlaneTilt as PaperPlaneIcon,
} from "@phosphor-icons/react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import { formatDay } from "@/lib/utils/date";
import { useYardContext } from "@/providers/yard-context";
import { ClasseurFilterBar, type FilterDef } from "../components/classeur";
import { useClasseurFilters } from "../components/classeur/use-classeur-data";
import { CompteRenduForm } from "../components/compte-rendu-form";

// Écran 005 « CR réunion client » : classeur des comptes-rendus de réunion
// client reçus, classés par n°.

export type CompteRenduItem =
  paths["/api/yards/{yardId}/comptes-rendus"]["get"]["responses"][200]["content"]["application/json"]["data"]["items"][number];

type FichierRef = CompteRenduItem["piecesJointes"][number];

type Recipient = CompteRenduItem["recipients"][number];

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "success"; items: CompteRenduItem[] };

const LOAD_ERROR = "Impossible de charger les comptes-rendus.";

function formatDate(iso: string | null): string {
  return isNil(iso) ? "-" : formatDay(iso);
}

function recipientLabel(recipient: Recipient): string {
  return isNil(recipient.sentAt)
    ? `${recipient.teamName} (envoi en attente)`
    : `${recipient.teamName} (${formatDate(recipient.sentAt)})`;
}

/** « x/y » of the mockup (N142, 5A). */
function distributionTitle(sent: number, total: number): string {
  return sent > 1
    ? `Distribution terminée pour ${sent} destinataires sur ${total}, courrier et remise en main propre compris`
    : `Distribution terminée pour ${sent} destinataire sur ${total}, courrier et remise en main propre compris`;
}

function diffuseTitle(item: CompteRenduItem): string {
  if (item.inDiffusion) {
    return "Déjà en attente dans « Diffuser vers prestataires » (liste ou bordereau en construction)";
  }
  if (isNil(item.fichier)) return "Aucun fichier à diffuser";
  return "Mettre en liste de diffusion vers prestataire";
}

const cellClass = "truncate px-2 text-center";

export function CRReunion() {
  const { yardId } = useParams<{ yardId: string }>();
  const { hasContractType } = useYardContext();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  // Bumped after a creation: refetches without going back to "loading", so
  // the table stays in place (screen-design.md).
  const [reloadKey, setReloadKey] = useState(0);
  const [diffusingId, setDiffusingId] = useState<number | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const { data, error } = await apiClient.GET(
          "/api/yards/{yardId}/comptes-rendus",
          { params: { path: { yardId: yardId ?? "" } } },
        );
        setState(
          isNotNil(data)
            ? { status: "success", items: data.data.items }
            : { status: "error", message: error?.error ?? LOAD_ERROR },
        );
      } catch (error) {
        console.error("Failed to load comptes-rendus", error);
        setState({ status: "error", message: LOAD_ERROR });
      }
    }
    fetchData();
  }, [yardId, reloadKey]);

  const items = state.status === "success" ? state.items : [];

  // Options come from the full list so they stay stable while filtering.
  const filters = useMemo<FilterDef[]>(
    () => [
      {
        key: "objet",
        label: "Objet",
        options: [...new Set(items.map((item) => item.object))].map(
          (object) => ({ value: object, label: object }),
        ),
      },
    ],
    [items],
  );

  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });
  const visibleItems = useMemo(() => {
    const objets = activeFilters.objet ?? [];
    return objets.length === 0
      ? items
      : items.filter((item) => objets.includes(item.object));
  }, [items, activeFilters]);

  async function openFichier(fichierId: number) {
    try {
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        {
          params: {
            path: { yardId: yardId ?? "", fichierId: String(fichierId) },
          },
        },
      );
      if (isNotNil(data)) {
        window.open(data.data.inlineUrl, "_blank");
        return;
      }
    } catch (error) {
      console.error("Failed to open compte-rendu file", error);
    }
    toaster.error({
      title: "Erreur",
      description: "Impossible d'ouvrir le fichier",
    });
  }

  async function addToDiffusion(item: CompteRenduItem) {
    setDiffusingId(item.id);
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/comptes-rendus/{compteRenduId}/diffusion",
        {
          params: {
            path: { yardId: yardId ?? "", compteRenduId: item.id },
          },
        },
      );
      if (isNil(error)) {
        toaster.success({
          title: "Compte-rendu mis en liste de diffusion",
          description: `Le compte-rendu n°${item.number} attend dans « Diffuser vers prestataires ».`,
        });
        setReloadKey((k) => k + 1);
        return;
      }
      toaster.error({ title: "Erreur", description: error.error });
    } catch (error) {
      console.error("Failed to put compte-rendu in diffusion", error);
      toaster.error({
        title: "Erreur",
        description: "Impossible de mettre le compte-rendu en diffusion",
      });
    } finally {
      setDiffusingId(null);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="flex shrink-0 items-start justify-between gap-4 border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            Classeur comptes-rendus réunions de chantier
          </h2>
          {state.status === "success" && (
            <p className="mt-1 text-sm text-zinc-500">
              {visibleItems.length > 1
                ? `${visibleItems.length} comptes-rendus`
                : `${visibleItems.length} compte-rendu`}
            </p>
          )}
        </div>
        {/* The creation route only accepts a travaux rang 1 contract. */}
        {hasContractType("travaux_n_1") && (
          <CompteRenduForm onCreated={() => setReloadKey((k) => k + 1)} />
        )}
      </header>

      <ClasseurFilterBar
        filters={filters}
        activeFilters={activeFilters}
        onFilterChange={onFilterChange}
      />

      <div className="flex min-h-0 flex-1 flex-col px-4 py-4">
        {state.status === "loading" ? (
          <p className="py-12 text-center text-sm text-zinc-500">
            Chargement des comptes-rendus…
          </p>
        ) : state.status === "error" ? (
          <p className="py-12 text-center text-sm text-red-600">
            {state.message}
          </p>
        ) : visibleItems.length === 0 ? (
          <p className="py-12 text-center text-sm text-zinc-500">
            Aucun compte-rendu
          </p>
        ) : (
          <div className="min-h-0 overflow-y-auto overflow-x-hidden rounded-lg border border-zinc-200 dark:border-zinc-700">
            <table className="w-full table-fixed border-collapse text-sm">
              {/* Widths: .agents/screen-column-widths.md, « Écran 005 ». */}
              <colgroup>
                <col className="w-16" />
                <col className="w-20" />
                <col className="w-32" />
                <col className="w-32" />
                {/* Chrome ignores calc() on a col: percentages hold 40 / 25 / 35
                    at 1280 px, and the object takes what is left. */}
                <col />
                <col style={{ width: "12.5%" }} />
                <col style={{ width: "17.5%" }} />
                <col className="w-24" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-zinc-50 text-xs dark:bg-zinc-800">
                <tr className="h-8 border-b border-zinc-200 dark:border-zinc-700">
                  <th className="px-2 text-center font-medium">Fichier</th>
                  <th className="px-2 text-center font-medium">N°</th>
                  <th className="px-2 text-center font-medium">
                    Date réception
                  </th>
                  <th className="px-2 text-center font-medium">Date réunion</th>
                  <th className="px-2 text-left font-medium">
                    Objet de la réunion
                  </th>
                  <th className="px-2 text-center font-medium">
                    Pièces jointes
                  </th>
                  <th
                    className="truncate px-1 text-left font-medium"
                    title="Transmission sous-traitants"
                  >
                    Transmission sous-traitants
                  </th>
                  <th
                    className="px-2 text-center font-medium"
                    title="Destinataires dont la distribution est terminée / destinataires définis"
                  >
                    Diffusion
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item) => {
                  const sentCount = item.recipients.filter((r) =>
                    isNotNil(r.sentAt),
                  ).length;
                  const transmissions = item.recipients
                    .map(recipientLabel)
                    .join(", ");
                  return (
                    <tr
                      key={item.id}
                      className="h-10 border-b border-zinc-100 dark:border-zinc-800"
                    >
                      <td className={cellClass}>
                        {isNotNil(item.fichier) ? (
                          <FichierButton
                            fichier={item.fichier}
                            label={`Ouvrir le compte-rendu n°${item.number}`}
                            onOpen={openFichier}
                          >
                            <FileTextIcon size={18} className="shrink-0" />
                          </FichierButton>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className={cellClass} title={item.number}>
                        {item.number}
                      </td>
                      <td className={cellClass}>
                        {formatDate(item.dateReception)}
                      </td>
                      <td className={cellClass}>
                        {formatDate(item.dateReunion)}
                      </td>
                      <td className="truncate px-2" title={item.object}>
                        {item.object}
                      </td>
                      <td className={cellClass}>
                        <PiecesJointes
                          piecesJointes={item.piecesJointes}
                          onOpen={openFichier}
                        />
                      </td>
                      <td
                        className="truncate px-2"
                        title={transmissions || undefined}
                      >
                        {transmissions || "-"}
                      </td>
                      <td className={cellClass}>
                        <div className="flex items-center justify-center gap-2">
                          {item.recipients.length > 0 ? (
                            <span
                              title={distributionTitle(
                                sentCount,
                                item.recipients.length,
                              )}
                            >
                              {sentCount}/{item.recipients.length}
                            </span>
                          ) : (
                            <span className="text-zinc-300">-</span>
                          )}
                          {/* The title sits on a wrapper: a disabled button
                              gets no hover events in every browser. */}
                          <span title={diffuseTitle(item)}>
                            <button
                              type="button"
                              onClick={() => addToDiffusion(item)}
                              disabled={
                                item.inDiffusion ||
                                isNil(item.fichier) ||
                                isNotNil(diffusingId)
                              }
                              aria-label={`Mettre le compte-rendu n°${item.number} en liste de diffusion vers prestataire`}
                              className="align-middle text-zinc-400 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-zinc-400 dark:hover:text-blue-400"
                            >
                              <PaperPlaneIcon size={16} />
                            </button>
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FichierButton({
  fichier,
  label,
  onOpen,
  children,
}: {
  fichier: FichierRef;
  label: string;
  onOpen: (fichierId: number) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(fichier.fichierId)}
      title={fichier.filename}
      aria-label={label}
      className="inline-flex min-w-0 max-w-full items-center text-blue-600 hover:underline dark:text-blue-400"
    >
      {children}
    </button>
  );
}

function PiecesJointes({
  piecesJointes,
  onOpen,
}: {
  piecesJointes: FichierRef[];
  onOpen: (fichierId: number) => void;
}) {
  if (piecesJointes.length === 0) return "-";
  return (
    // Links stay on one line so the row keeps its fixed height.
    <div
      className="flex items-center justify-center gap-2 overflow-hidden"
      title={piecesJointes.map((pj) => pj.filename).join(", ")}
    >
      <PaperclipIcon size={14} className="shrink-0 text-zinc-500" />
      {piecesJointes.map((pj) => (
        <FichierButton
          key={pj.fichierId}
          fichier={pj}
          label={`Ouvrir la pièce jointe ${pj.filename}`}
          onOpen={onOpen}
        >
          <span className="truncate">{pj.filename}</span>
        </FichierButton>
      ))}
    </div>
  );
}
